import type { RouteLink } from "../lib/routes";

interface RouteLinksProps {
  title: string;
  routes: RouteLink[];
  /** Visual style: "chip" pills (default) or a compact inline list. */
  variant?: "chip" | "inline";
}

/**
 * Renders a titled group of crawlable internal route links.
 * Pure/presentational — server-rendered into initial HTML for SEO.
 * Uses plain <a> (full-page navigation) rather than next/link: the app
 * navigates via full loads, and Bun's dev runtime trips over a bare
 * require('path') in Next 16's client-navigation render path.
 */
export default function RouteLinks({ title, routes, variant = "chip" }: RouteLinksProps) {
  if (routes.length === 0) return null;

  if (variant === "inline") {
    return (
      <div>
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
          {title}
        </h3>
        <ul className="mt-2 flex flex-wrap gap-x-2 gap-y-1">
          {routes.map((r) => (
            <li key={r.slug}>
              <a
                href={`/${r.slug}`}
                className="inline-flex items-center min-h-[24px] px-1.5 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
              >
                {r.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
        {title}
      </h2>
      <ul className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
        {routes.map((r) => (
          <li key={r.slug}>
            <a
              href={`/${r.slug}`}
              className="group flex items-center justify-between gap-2 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:border-blue-400 dark:hover:border-blue-500/50 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <span>{r.label} Bus Timetable</span>
              <span className="text-slate-300 dark:text-slate-600 group-hover:text-blue-500 transition-colors" aria-hidden="true">
                →
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
